import { Fragment, useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion, type Transition } from 'motion/react';
import type { CommitDetailProps } from '../contracts';
import { ease, prefersReducedMotion, spring } from '../../lib/motion';
import { fullDateTime } from '../../lib/format';
import { Machine, Parts } from '../../ui';
import { useStrings } from '../../state/useLocale';

/**
 * The commit detail strip.
 *
 * A drawer under the graph that opens on the commit you picked and closes
 * when you let it go. It is the only place in the stage that spells a hash
 * out in full, because it is the only place you would ever copy one from.
 *
 * The height is measured rather than animated to `auto`: `auto` resolves once,
 * and this strip changes content while it is open every time you click a
 * different commit. Measuring means the drawer resizes to whatever the next
 * commit needs instead of keeping the first one's height. Vertical padding
 * lives on the inner element so that a closed drawer is genuinely zero tall.
 */

const INSTANT: Transition = { duration: 0 };
const PAD = { paddingTop: 'var(--sp-3)', paddingBottom: 'var(--sp-3)' } as const;

export function CommitDetail({ git, hash }: CommitDetailProps) {
  const s = useStrings().play.commit;
  const reduced = (useReducedMotion() ?? prefersReducedMotion()) === true;
  const [inner, setInner] = useState<HTMLDivElement | null>(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (!inner) return;
    const measure = () => setHeight(inner.offsetHeight);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(inner);
    return () => observer.disconnect();
  }, [inner]);

  const commit = hash ? git.commits.find((c) => c.hash === hash) : undefined;
  const refs = commit
    ? git.refs.filter((r) => r.hash === commit.hash && r.kind !== 'other').map((r) => r.short)
    : [];
  // Was a bare `toLocaleString()`, which took whatever language the browser
  // was set to and ignored the page's — so an Arabic panel could print an
  // English date, or Arabic-Indic digits next to the Western ones the
  // terminal prints. lib/format pins both.
  const date = commit?.time ? fullDateTime(new Date(commit.time * 1000)) : '';

  return (
    <AnimatePresence initial={false}>
      {commit && (
        <motion.div
          key="detail"
          className="detail"
          // The drawer's own vertical padding would keep a closed drawer 24px
          // tall, so it moves inside where the measured height can swallow it.
          style={{ paddingTop: 0, paddingBottom: 0 }}
          initial={{ height: 0, opacity: 0 }}
          animate={{ height, opacity: 1 }}
          exit={{ height: 0, opacity: 0, transition: reduced ? INSTANT : ease }}
          transition={reduced ? INSTANT : spring}
        >
          <div ref={setInner} style={PAD}>
            {/* The keys are labels on machine fields, so they translate as
                prose — except `commit`, which is the noun the learner types
                (docs/i18n.md §5). The values are all the machine's and keep
                their own direction. */}
            <div className="detail-row">
              <span className="detail-key">{s.keyCommit}</span>
              <code dir="ltr">{commit.hash}</code>
            </div>

            <div className="detail-row">
              <span className="detail-key">{s.keySubject}</span>
              <span dir="ltr">{commit.subject}</span>
            </div>

            <div className="detail-row">
              <span className="detail-key">{s.keyAuthor}</span>
              <span>
                {date ? (
                  <Parts of={s.authorAndDate(<Machine key="author">{commit.author}</Machine>, date)} />
                ) : (
                  <Machine>{commit.author}</Machine>
                )}
              </span>
            </div>

            <div className="detail-row">
              <span className="detail-key">{s.keyParents}</span>
              {/* The hashes are <code>, which locale.css already isolates, so
                  the row can stay in the page's direction and the sentence
                  that replaces them when there are none reads as prose. */}
              <span>
                {commit.parents.length ? (
                  commit.parents.map((p, i) => (
                    <Fragment key={p}>
                      {i > 0 && ' '}
                      <code>{p.slice(0, 7)}</code>
                    </Fragment>
                  ))
                ) : (
                  <em>{s.noParents}</em>
                )}
              </span>
            </div>

            <div className="detail-row">
              <span className="detail-key">{s.keyRefs}</span>
              <span>
                {refs.length ? (
                  refs.map((r, i) => (
                    <Fragment key={r}>
                      {i > 0 && ' '}
                      <code>{r}</code>
                    </Fragment>
                  ))
                ) : (
                  <em>{commit.reachable ? s.refsReachable : s.refsUnreachable}</em>
                )}
              </span>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default CommitDetail;
